import { useState } from 'react';
import { Bell, BellOff, LogOut } from 'lucide-react';
import clsx from 'clsx';
import { useAuthStore } from '../store/useAuthStore';
import { usePushStore } from '../store/notificationStore';
import { registerServiceWorker } from '../services/pushService';

interface UserMenuDropdownProps {
  email: string | null;
  onLogout: () => void;
  position: 'top' | 'bottom';
}

const UserMenuDropdown: React.FC<UserMenuDropdownProps> = ({ email, onLogout, position }) => {
  const subscribed = useAuthStore((state) => state.subscribed);
  const { subscribeToPush, unsubscribeFromPush } = usePushStore();
  const [pushLoading, setPushLoading] = useState(false);

  const handleTogglePush = async () => {
    setPushLoading(true);
    try {
      const reg = await registerServiceWorker();
      if (!reg) return;
      if (subscribed) {
        await unsubscribeFromPush(reg);
        useAuthStore.setState({ subscribed: false });
      } else {
        await subscribeToPush(reg);
        useAuthStore.setState({ subscribed: true });
      }
    } finally {
      setPushLoading(false);
    }
  };

  return (
    <div
      className={clsx(
        'absolute right-0 z-50 w-64 rounded-xl border border-edge bg-panel shadow-panel transition-all duration-200',
        position === 'top' ? 'bottom-full mb-3' : 'top-full mt-3',
      )}
    >
      <div className="border-b border-edge px-5 py-4">
        <p className="truncate text-sm font-medium text-body">{email || 'No email available'}</p>
      </div>

      <button
        onClick={handleTogglePush}
        disabled={pushLoading}
        data-testid="test-push-toggle"
        className="flex w-full items-center space-x-3 px-5 py-3 text-sm text-body transition-colors hover:bg-elevated disabled:opacity-50"
      >
        {subscribed ? <BellOff className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
        <span>{subscribed ? 'Disable notifications' : 'Enable notifications'}</span>
      </button>

      <button
        onClick={onLogout}
        data-testid="test-logout"
        className="flex w-full items-center space-x-3 rounded-b-xl px-5 py-3 text-sm text-red-500 transition-colors hover:bg-elevated"
      >
        <LogOut className="h-4 w-4" />
        <span>Sign out</span>
      </button>
    </div>
  );
};

export default UserMenuDropdown;
